import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { db } from '../firebase/firebase';
import { collection, getDocs, query, orderBy, limit, getDoc, doc as fsDoc } from 'firebase/firestore';
import DateRangePicker from './DateRangePicker';

// ─── layout constants ────────────────────────────────────────────────────────
const ROW_H       = 48;
const HDR_TOP_H   = 26;   // month / year strip
const HDR_BOT_H   = 34;   // day / week strip
const HEADER_H    = HDR_TOP_H + HDR_BOT_H;
const SIDEBAR_W   = 300;
const DAY_WIDTHS  = { day: 40, week: 20, month: 10, quarter: 7 };
const PHASE_COLORS = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6','#f97316'];

function normalizePhases(phases) {
  return (phases || []).map((p, i) =>
    typeof p === 'string' ? { name: p, color: PHASE_COLORS[i % PHASE_COLORS.length] } : p
  );
}

// ─── date helpers ─────────────────────────────────────────────────────────────
function parseDate(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function fmtDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function addDays(date, n) {
  const d = new Date(date); d.setDate(d.getDate() + n); return d;
}
function diffDays(a, b) {
  const a0 = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const b0 = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((b0 - a0) / 86400000);
}
function todayMidnight() {
  const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}
function fmtShort(iso) {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d} ${months[parseInt(m,10)-1]}`;
}

// ─── task status ─────────────────────────────────────────────────────────────
function getTaskStatus(task, today) {
  if (task.isMilestone) return 'milestone';
  if (!task.start || !task.end) return 'normal';
  const s = parseDate(task.start);
  const e = parseDate(task.end);
  const status = task.status || 'not_started';
  if (status === 'complete') return 'complete';
  if (today < s) return 'upcoming';
  if (today > e) return 'overdue';
  if (status === 'not_started' && today >= s) return 'atrisk';
  return 'normal';
}

// ─── critical path (CPM float = 0) ───────────────────────────────────────────
// Normalize a dependency entry — supports legacy string IDs and new {id,type,lag} objects
function normDep(dep) {
  if (typeof dep === 'string') return { id: dep, type: 'FS', lag: 0 };
  return { id: dep.id, type: dep.type || 'FS', lag: dep.lag || 0 };
}

function computeScheduleData(tasks) {
  const taskMap = Object.fromEntries(tasks.map(t => [t.id, t]));
  const valid   = tasks.filter(t => t.start && t.end && !t.isMilestone);
  if (!valid.length) return { criticalIds: new Set(), floatDays: new Map() };

  // Build successor map: predId → [{succId, type, lag}]
  const successors = Object.fromEntries(tasks.map(t => [t.id, []]));
  tasks.forEach(t => {
    (t.dependsOn || []).forEach(raw => {
      const { id, type, lag } = normDep(raw);
      if (successors[id]) successors[id].push({ succId: t.id, type, lag });
    });
  });

  const projectEnd = valid.reduce((mx, t) => {
    const e = parseDate(t.end); return e > mx ? e : mx;
  }, parseDate(valid[0].end));

  const floatDays = new Map();
  valid.forEach(task => {
    // Default float: days from task end to project end
    let float = diffDays(parseDate(task.end), projectEnd);
    for (const { succId, type, lag } of (successors[task.id] || [])) {
      const succ = taskMap[succId];
      if (!succ?.start) continue;
      let slack;
      switch (type) {
        case 'SS': slack = diffDays(parseDate(task.start), parseDate(succ.start)) - lag; break;
        case 'FF': slack = succ.end ? diffDays(parseDate(task.end),   parseDate(succ.end))   - lag : float; break;
        case 'SF': slack = succ.end ? diffDays(parseDate(task.start), parseDate(succ.end))   - lag : float; break;
        default:   slack = diffDays(parseDate(task.end),   parseDate(succ.start)) - lag; break; // FS
      }
      if (slack < float) float = slack;
    }
    floatDays.set(task.id, float);
  });

  const criticalIds = new Set([...floatDays.entries()].filter(([, f]) => f <= 0).map(([id]) => id));
  return { criticalIds, floatDays };
}

// ─── header data builder ─────────────────────────────────────────────────────
const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function buildHeaderData(viewStart, totalDays, dayW, viewMode) {
  const topRow = [];
  const botRow = [];

  if (viewMode === 'week') {
    // Top row: one cell per week, labeled with the week-start date ("9 Jun")
    for (let j = 0; j < totalDays; j += 7) {
      const d = addDays(viewStart, j);
      const w = Math.min(7, totalDays - j);
      topRow.push({ left: j * dayW, width: w * dayW, label: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) });
    }
    // Bottom row: individual day columns with single-letter day name
    for (let j = 0; j < totalDays; j++) {
      const d = addDays(viewStart, j);
      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
      botRow.push({ left: j * dayW, width: dayW, label: DAY_LETTERS[d.getDay()], isWeekend });
    }
  } else {
    // Top row: month / year groups (day and quarter modes)
    let i = 0;
    while (i < totalDays) {
      const d = addDays(viewStart, i);
      const mo = d.getMonth(), yr = d.getFullYear();
      let count = 0;
      while (i + count < totalDays) {
        const nd = addDays(viewStart, i + count);
        if (nd.getMonth() !== mo || nd.getFullYear() !== yr) break;
        count++;
      }
      topRow.push({ left: i * dayW, width: count * dayW, label: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) });
      i += count;
    }
    // Bottom row
    if (viewMode === 'day') {
      for (let j = 0; j < totalDays; j++) {
        const d = addDays(viewStart, j);
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        botRow.push({ left: j * dayW, width: dayW, label: d.getDate(), isWeekend });
      }
    } else {
      for (let j = 0; j < totalDays; j += 7) {
        const d = addDays(viewStart, j);
        const w = Math.min(7, totalDays - j);
        botRow.push({ left: j * dayW, width: w * dayW, label: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) });
      }
    }
  }

  return { topRow, botRow };
}

// ─── GanttChart ───────────────────────────────────────────────────────────────
export default function GanttChart({ tasks = [], teams = [], onTaskSave, onTaskDelete, projectId, phases = [], onPhasesChange, areas = [], onAreasChange, onExport, exporting = false, locked = true, onToggleLock, onShowHistory, revisionLabel = null }) {
  const [viewMode, setViewMode]         = useState('week');
  const [dragging, setDragging]         = useState(null);
  const [editTask, setEditTask]         = useState(null);
  const [hoveredRow, setHoveredRow]     = useState(null);
  const [tooltip, setTooltip]           = useState(null);
  const [collapsedPhases, setCollapsedPhases] = useState(new Set());
  const [showOrganise, setShowOrganise]   = useState(false);
  const [organiseTab, setOrganiseTab]     = useState('phases');
  const [newPhaseName, setNewPhaseName]   = useState('');
  const [newPhaseColor, setNewPhaseColor] = useState(PHASE_COLORS[0]);
  const [bodyScrollLeft, setBodyScrollLeft] = useState(0);
  const [bodyClientW, setBodyClientW]       = useState(0);
  const [contextMenu, setContextMenu]       = useState(null);
  const [newAreaName, setNewAreaName]       = useState('');
  const organiseMgrRef = useRef(null);

  const bodyRef        = useRef(null);
  const headerInnerRef = useRef(null);
  const wasDragged     = useRef(false);
  const dayW           = DAY_WIDTHS[viewMode];

  // ── date range ──────────────────────────────────────────────────────────────
  const { viewStart, totalDays } = useMemo(() => {
    const base = todayMidnight();
    const minDays = viewMode === 'quarter' ? 270 : viewMode === 'month' ? 180 : 90;
    if (tasks.length === 0) return { viewStart: addDays(base, -14), totalDays: minDays };
    let mn = null, mx = null;
    for (const t of tasks) {
      if (!t.start) continue;
      const s = parseDate(t.start);
      if (!mn || s < mn) mn = s;
      const endStr = (!t.isMilestone && t.end) ? t.end : t.start;
      const e = parseDate(endStr);
      if (!mx || e > mx) mx = e;
    }
    if (!mn) return { viewStart: addDays(base, -14), totalDays: minDays };
    const start = addDays(mn, -14);
    const end   = addDays(mx, 28);
    return { viewStart: start, totalDays: Math.max(minDays, diffDays(start, end)) };
  }, [tasks, viewMode]);

  const totalW    = totalDays * dayW;
  const todayd    = todayMidnight();
  const todayPx   = diffDays(viewStart, todayd) * dayW;
  const { topRow, botRow } = useMemo(
    () => buildHeaderData(viewStart, totalDays, dayW, viewMode),
    [viewStart, totalDays, dayW, viewMode]
  );
  const { criticalIds, floatDays } = useMemo(() => computeScheduleData(tasks), [tasks]);

  const normPhases = useMemo(() => normalizePhases(phases), [phases]);

  // ── display items (phases + tasks flattened) ────────────────────────────────
  const displayItems = useMemo(() => {
    if (!normPhases.length) {
      return tasks.map(task => ({ type: 'task', task }));
    }
    const items = [];
    const byPhase = {};
    const ungrouped = [];
    for (const task of tasks) {
      if (task.phase && normPhases.some(p => p.name === task.phase)) {
        if (!byPhase[task.phase]) byPhase[task.phase] = [];
        byPhase[task.phase].push(task);
      } else {
        ungrouped.push(task);
      }
    }
    const phaseRange = (taskList) => {
      let phaseStart = null, phaseEnd = null;
      for (const t of taskList) {
        if (!t.start) continue;
        if (!phaseStart || t.start < phaseStart) phaseStart = t.start;
        const end = (!t.isMilestone && t.end) ? t.end : t.start;
        if (!phaseEnd || end > phaseEnd) phaseEnd = end;
      }
      return { phaseStart, phaseEnd };
    };
    for (const { name, color } of normPhases) {
      const phaseTasks = byPhase[name] || [];
      items.push({ type: 'phase', phase: name, count: phaseTasks.length, color, ...phaseRange(phaseTasks) });
      if (!collapsedPhases.has(name)) {
        phaseTasks.forEach(task => items.push({ type: 'task', task }));
      }
    }
    if (ungrouped.length > 0) {
      items.push({ type: 'phase', phase: '__ungrouped__', count: ungrouped.length, color: '#94a3b8', ...phaseRange(ungrouped) });
      if (!collapsedPhases.has('__ungrouped__')) {
        ungrouped.forEach(task => items.push({ type: 'task', task }));
      }
    }
    return items;
  }, [tasks, normPhases, collapsedPhases]);

  // ── sync header scroll + minimap ────────────────────────────────────────────
  const onBodyScroll = (e) => {
    const sl = e.target.scrollLeft;
    if (headerInnerRef.current) headerInnerRef.current.style.transform = `translateX(-${sl}px)`;
    setBodyScrollLeft(sl);
  };

  const scrollToToday = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const x = Math.max(0, todayPx - 300);
    el.scrollLeft = x;
    if (headerInnerRef.current) headerInnerRef.current.style.transform = `translateX(-${x}px)`;
    setBodyScrollLeft(x);
  }, [todayPx]);

  // measure body width for minimap viewport indicator
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    setBodyClientW(el.clientWidth);
    const ro = new ResizeObserver(() => { if (bodyRef.current) setBodyClientW(bodyRef.current.clientWidth); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const seekMinimap = useCallback((ratio) => {
    const el = bodyRef.current;
    if (!el) return;
    const x = Math.max(0, Math.min(ratio * totalW - el.clientWidth / 2, totalW - el.clientWidth));
    el.scrollLeft = x;
    if (headerInnerRef.current) headerInnerRef.current.style.transform = `translateX(-${x}px)`;
    setBodyScrollLeft(x);
  }, [totalW]);

  const onMinimapPointerDown = useCallback((e) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const seek = (cx) => seekMinimap((cx - rect.left) / rect.width);
    seek(e.clientX);
    const onMove = (me) => seek(me.clientX);
    const onUp   = () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [seekMinimap]);

  useEffect(() => { scrollToToday(); }, [viewMode]); // eslint-disable-line

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { if (editTask !== null) setEditTask(null); return; }
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      if (editTask !== null) return;
      if (e.key === 't' || e.key === 'T') scrollToToday();
      if (e.key === 'd' || e.key === 'D') setViewMode('day');
      if (e.key === 'w' || e.key === 'W') setViewMode('week');
      if (e.key === 'm' || e.key === 'M') setViewMode('month');
      if (e.key === 'q' || e.key === 'Q') setViewMode('quarter');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [scrollToToday, editTask]);

  // ── context menu ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') setContextMenu(null); };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('pointerdown', close); window.removeEventListener('keydown', onKey); };
  }, [contextMenu]);

  const onBarContextMenu = (e, task) => {
    e.preventDefault(); e.stopPropagation();
    if (locked) return;
    setContextMenu({ task, x: e.clientX, y: e.clientY });
  };

  useEffect(() => {
    if (!showOrganise) return;
    const onDown = (e) => { if (organiseMgrRef.current && !organiseMgrRef.current.contains(e.target)) setShowOrganise(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setShowOrganise(false); };
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('pointerdown', onDown); window.removeEventListener('keydown', onKey); };
  }, [showOrganise]);

  // ── drag ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!dragging) return;
    const { startX, taskId, type } = dragging;
    const onMove = (e) => {
      const pd = Math.round((e.clientX - startX) / dayW);
      setDragging(prev => prev ? { ...prev, pendingDays: pd } : null);
    };
    const onUp = (e) => {
      const delta = Math.round((e.clientX - startX) / dayW);
      if (Math.abs(delta) > 0) {
        wasDragged.current = true;
        const task = tasks.find(t => t.id === taskId);
        if (task && onTaskSave) {
          const s = parseDate(task.start), en = parseDate(task.end);
          if (type === 'move') {
            onTaskSave({ ...task, start: fmtDate(addDays(s, delta)), end: fmtDate(addDays(en, delta)) });
          } else {
            const newEnd = addDays(en, delta);
            onTaskSave({ ...task, end: fmtDate(newEnd <= s ? s : newEnd) });
          }
        }
      }
      setDragging(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
  }, [dragging, dayW, tasks, onTaskSave]);

  const onBarPointerDown = (e, task, type) => {
    e.preventDefault(); e.stopPropagation();
    if (locked) return;
    wasDragged.current = false;
    setTooltip(null);
    setDragging({ taskId: task.id, type, startX: e.clientX, pendingDays: 0 });
  };

  const onBarClick = (task) => {
    if (locked) return;
    if (wasDragged.current) { wasDragged.current = false; return; }
    setEditTask(task);
  };

  // ── shared styles ────────────────────────────────────────────────────────────
  const borderColor = 'var(--branding-app-surface-panel-border)';
  const panelBg     = 'var(--branding-app-surface-panel)';
  const cardBg      = 'var(--cc-card-bg)';
  const hasDeps     = floatDays.size > 0 && tasks.some(t => t.dependsOn?.length > 0);
  const hasAnyBaseline = tasks.some(t => t.baseline);

  const isMonthMode = viewMode === 'month' || viewMode === 'quarter';

  return (
    <div style={{ fontFamily: 'inherit', userSelect: dragging ? 'none' : undefined }}>

      {/* ── Toolbar ───────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>

        {/* Left: Add Task + Phases + Areas */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setEditTask({})}
            style={{
              background: 'var(--branding-app-primary)',
              color: 'var(--branding-app-primary-fg)',
              border: 'none',
              borderRadius: 9,
              padding: '9px 20px',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: 13,
              letterSpacing: '0.01em',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              boxShadow: '0 2px 8px rgba(0,0,0,0.14)',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add Task
          </button>

          {/* Organise dropdown — Phases + Areas */}
          {(onPhasesChange || onAreasChange) && (
            <div style={{ position: 'relative' }} ref={organiseMgrRef}>
              <button
                onClick={() => setShowOrganise(v => !v)}
                style={{
                  background: showOrganise ? 'var(--branding-app-primary)' : 'transparent',
                  color: showOrganise ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-secondary)',
                  border: `1.5px solid ${showOrganise ? 'var(--branding-app-primary)' : 'var(--branding-app-surface-panel-border)'}`,
                  borderRadius: 9, padding: '8px 14px',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 6,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { if (!showOrganise) { e.currentTarget.style.borderColor = 'var(--branding-app-primary)'; e.currentTarget.style.color = 'var(--branding-app-primary)'; }}}
                onMouseLeave={e => { if (!showOrganise) { e.currentTarget.style.borderColor = 'var(--branding-app-surface-panel-border)'; e.currentTarget.style.color = 'var(--cc-text-secondary)'; }}}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                </svg>
                Organise
              </button>

              {showOrganise && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 8px)', left: 0,
                  background: 'var(--branding-app-surface-panel)',
                  border: '1.5px solid var(--branding-app-surface-panel-border)',
                  borderRadius: 12, width: 272, zIndex: 200,
                  boxShadow: '0 8px 28px rgba(0,0,0,0.14)', overflow: 'hidden',
                }}>
                  {/* Tabs */}
                  {onPhasesChange && onAreasChange && (
                    <div style={{ display: 'flex', borderBottom: '1px solid var(--branding-app-surface-panel-border)', padding: '4px 8px 0' }}>
                      {[{ id: 'phases', label: 'Phases' }, { id: 'areas', label: 'Areas' }].map(tab => (
                        <button key={tab.id} onClick={() => setOrganiseTab(tab.id)} style={{
                          padding: '7px 12px', border: 'none', cursor: 'pointer', fontSize: 12,
                          fontWeight: organiseTab === tab.id ? 700 : 500, background: 'transparent',
                          color: organiseTab === tab.id ? 'var(--branding-app-primary)' : 'var(--cc-text-muted)',
                          borderBottom: organiseTab === tab.id ? '2px solid var(--branding-app-primary)' : '2px solid transparent',
                          marginBottom: -1, transition: 'all 0.12s',
                        }}>{tab.label}</button>
                      ))}
                    </div>
                  )}

                  <div style={{ padding: 12 }}>
                    {/* Phases tab */}
                    {(organiseTab === 'phases' || !onAreasChange) && onPhasesChange && (
                      <>
                        {normPhases.length === 0 && <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--cc-text-muted)', fontStyle: 'italic' }}>No phases yet</p>}
                        {normPhases.map(phase => (
                          <div key={phase.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 2px', borderBottom: '1px solid var(--branding-app-surface-panel-border)' }}>
                            <label title="Change colour" style={{ width: 16, height: 16, borderRadius: 4, background: phase.color, cursor: 'pointer', flexShrink: 0, position: 'relative', display: 'block', boxShadow: '0 0 0 1.5px rgba(0,0,0,0.12)' }}>
                              <input type="color" value={phase.color} onChange={e => onPhasesChange(normPhases.map(p => p.name === phase.name ? { ...p, color: e.target.value } : p))} style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer', padding: 0, border: 'none' }} />
                            </label>
                            <span style={{ flex: 1, fontSize: 13, color: 'var(--cc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{phase.name}</span>
                            <button onClick={() => onPhasesChange(normPhases.filter(p => p.name !== phase.name))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cc-text-secondary)', padding: '2px 4px', borderRadius: 4, lineHeight: 1, fontSize: 14 }}>×</button>
                          </div>
                        ))}
                        <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
                          <label title="Pick colour" style={{ width: 30, height: 30, borderRadius: 7, background: newPhaseColor, cursor: 'pointer', flexShrink: 0, position: 'relative', display: 'block', boxShadow: `0 0 0 2px ${newPhaseColor}55, 0 0 0 3.5px rgba(0,0,0,0.1)` }}>
                            <input type="color" value={newPhaseColor} onChange={e => setNewPhaseColor(e.target.value)} style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer', padding: 0, border: 'none' }} />
                          </label>
                          <input value={newPhaseName} onChange={e => setNewPhaseName(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && newPhaseName.trim()) { if (!normPhases.some(p => p.name === newPhaseName.trim())) onPhasesChange([...normPhases, { name: newPhaseName.trim(), color: newPhaseColor }]); setNewPhaseName(''); setNewPhaseColor(PHASE_COLORS[normPhases.length % PHASE_COLORS.length]); }
                              if (e.key === 'Escape') setShowOrganise(false);
                            }}
                            placeholder="Phase name…"
                            style={{ flex: 1, padding: '7px 10px', borderRadius: 8, fontSize: 13, border: '1.5px solid var(--branding-app-surface-panel-border)', background: 'var(--branding-app-surface)', color: 'var(--cc-text-primary)', outline: 'none', fontFamily: 'inherit' }}
                          />
                          <button onClick={() => { if (newPhaseName.trim() && !normPhases.some(p => p.name === newPhaseName.trim())) onPhasesChange([...normPhases, { name: newPhaseName.trim(), color: newPhaseColor }]); setNewPhaseName(''); setNewPhaseColor(PHASE_COLORS[normPhases.length % PHASE_COLORS.length]); }} style={{ padding: '7px 11px', borderRadius: 8, fontSize: 13, fontWeight: 700, background: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)', border: 'none', cursor: 'pointer' }}>+</button>
                        </div>
                      </>
                    )}

                    {/* Areas tab */}
                    {(organiseTab === 'areas' || !onPhasesChange) && onAreasChange && (
                      <>
                        {areas.map(a => (
                          <div key={a} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 2px', borderBottom: '1px solid var(--branding-app-surface-panel-border)' }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--cc-text-secondary)" strokeWidth="2.5" strokeLinecap="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                            <span style={{ flex: 1, fontSize: 13, color: 'var(--cc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a}</span>
                            <button onClick={() => onAreasChange(areas.filter(x => x !== a))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cc-text-secondary)', padding: '2px 4px', borderRadius: 4, lineHeight: 1, fontSize: 14 }}>×</button>
                          </div>
                        ))}
                        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                          <input value={newAreaName} onChange={e => setNewAreaName(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && newAreaName.trim()) { if (!areas.includes(newAreaName.trim())) onAreasChange([...areas, newAreaName.trim()]); setNewAreaName(''); }
                              if (e.key === 'Escape') setShowOrganise(false);
                            }}
                            placeholder="Add area…"
                            style={{ flex: 1, padding: '7px 10px', borderRadius: 8, fontSize: 13, border: '1.5px solid var(--branding-app-surface-panel-border)', background: 'var(--branding-app-surface)', color: 'var(--cc-text-primary)', outline: 'none', fontFamily: 'inherit' }}
                          />
                          <button onClick={() => { if (newAreaName.trim() && !areas.includes(newAreaName.trim())) onAreasChange([...areas, newAreaName.trim()]); setNewAreaName(''); }} style={{ padding: '7px 11px', borderRadius: 8, fontSize: 13, fontWeight: 700, background: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)', border: 'none', cursor: 'pointer' }}>+</button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Right: Critical path legend + Today + view mode */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {criticalIds.size > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 11px', borderRadius: 8,
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.3)',
              fontSize: 11, fontWeight: 700,
              color: '#ef4444', letterSpacing: '0.04em',
              textTransform: 'uppercase',
              userSelect: 'none',
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
              Critical Path
            </div>
          )}
          {/* Revision badge */}
          {revisionLabel && (
            <div
              title={`Current programme revision: ${revisionLabel}`}
              style={{
                padding: '4px 10px', borderRadius: 7,
                background: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)',
                fontSize: 11, fontWeight: 800, letterSpacing: '0.04em',
                whiteSpace: 'nowrap', userSelect: 'none',
              }}
            >
              {revisionLabel}
            </div>
          )}
          {/* Lock toggle */}
          {onToggleLock && (
            <button
              onClick={onToggleLock}
              title={locked ? 'Unlock to edit' : 'Lock schedule'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: 8, cursor: 'pointer',
                transition: 'all 0.15s',
                border: locked
                  ? '1.5px solid var(--branding-app-primary)'
                  : '1.5px solid #10b981',
                background: locked ? 'var(--branding-app-primary)' : 'transparent',
                color: locked ? 'var(--branding-app-primary-fg)' : '#10b981',
              }}
            >
              {locked ? (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              ) : (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/>
                </svg>
              )}
            </button>
          )}

          {/* History */}
          {onShowHistory && (
            <button
              onClick={onShowHistory}
              title="View change history"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: 8, cursor: 'pointer',
                transition: 'all 0.15s',
                border: `1.5px solid ${borderColor}`,
                background: 'transparent', color: 'var(--cc-text-secondary)',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--branding-app-primary)'; e.currentTarget.style.color = 'var(--branding-app-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = borderColor; e.currentTarget.style.color = 'var(--cc-text-secondary)'; }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 9 15"/>
              </svg>
            </button>
          )}

          {onExport && (
            <button
              onClick={onExport}
              disabled={exporting}
              title="Export schedule"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: 8,
                cursor: exporting ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
                background: exporting ? 'rgba(0,0,0,0.06)' : 'transparent',
                color: 'var(--cc-text-secondary)',
                border: `1.5px solid ${borderColor}`,
                opacity: exporting ? 0.6 : 1,
              }}
              onMouseEnter={e => { if (!exporting) { e.currentTarget.style.borderColor = 'var(--branding-app-primary)'; e.currentTarget.style.color = 'var(--branding-app-primary)'; }}}
              onMouseLeave={e => { e.currentTarget.style.borderColor = borderColor; e.currentTarget.style.color = 'var(--cc-text-secondary)'; }}
            >
              {exporting ? (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 1s linear infinite' }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
              ) : (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              )}
            </button>
          )}
          <button
            onClick={scrollToToday}
            title="Scroll to today"
            style={{
              background: 'transparent',
              color: 'var(--branding-app-primary)',
              border: '1.5px solid var(--branding-app-primary)',
              borderRadius: 8,
              padding: '7px 14px',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--branding-app-primary)'; e.currentTarget.style.color = 'var(--branding-app-primary-fg)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--branding-app-primary)'; }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 8 12 12 14 14"/>
            </svg>
            Today
          </button>

          {/* View mode pills */}
          <div style={{ display: 'flex', background: panelBg, borderRadius: 9, padding: 3, border: `1px solid ${borderColor}` }}>
            {['day','week','month','quarter'].map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                style={{
                  background: viewMode === mode ? 'var(--branding-app-primary)' : 'transparent',
                  color: viewMode === mode ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-secondary)',
                  border: 'none',
                  borderRadius: 7,
                  padding: '6px 14px',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: viewMode === mode ? 700 : 500,
                  letterSpacing: viewMode === mode ? '0.02em' : 0,
                  transition: 'all 0.18s',
                  boxShadow: viewMode === mode ? '0 1px 4px rgba(0,0,0,0.15)' : 'none',
                }}
              >
                {{ day: 'Day', week: 'Week', month: 'Month', quarter: 'Quarter' }[mode]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Chart container ───────────────────────────────────────────────── */}
      <div style={{
        border: `1px solid ${borderColor}`,
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}>

        {/* ── Dual-row header ──────────────────────────────────────────── */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${borderColor}` }}>

          {/* Sidebar header — column labels anchored to bottom, matching timeline divider position */}
          <div style={{
            width: SIDEBAR_W, flexShrink: 0, height: HEADER_H,
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            boxSizing: 'border-box', borderRight: `1px solid ${borderColor}`,
            background: panelBg,
          }}>
            <div style={{
              height: HDR_BOT_H, display: 'flex', alignItems: 'center',
              paddingLeft: normPhases.length > 0 ? 20 : 14, paddingRight: 14,
              gap: 8, borderTop: `1px solid ${borderColor}`,
            }}>
              <span style={{ flex: 1, minWidth: 60, fontSize: 10, fontWeight: 800, color: 'var(--cc-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Task</span>
              <span style={{ width: 40, flexShrink: 0, fontSize: 10, fontWeight: 800, color: 'var(--cc-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'right' }}>Dur</span>
              {hasDeps && <span style={{ width: 36, flexShrink: 0, fontSize: 10, fontWeight: 800, color: 'var(--cc-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'right' }}>Float</span>}
              {hasAnyBaseline && <span style={{ width: 40, flexShrink: 0, fontSize: 10, fontWeight: 800, color: 'var(--cc-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'right' }}>ΔS</span>}
              {hasAnyBaseline && <span style={{ width: 40, flexShrink: 0, fontSize: 10, fontWeight: 800, color: 'var(--cc-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'right' }}>ΔF</span>}
            </div>
          </div>

          {/* Timeline header — transform-synced */}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <div ref={headerInnerRef} style={{ position: 'relative', height: HEADER_H, width: totalW }}>

              {/* Top row: month / year */}
              {topRow.map((cell, idx) => (
                <div key={idx} style={{
                  position: 'absolute',
                  left: cell.left,
                  top: 0,
                  width: cell.width,
                  height: HDR_TOP_H,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  paddingLeft: isMonthMode ? 0 : 10,
                  borderRight: `1px solid ${borderColor}`,
                  background: panelBg,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--cc-text-secondary)', letterSpacing: '0.02em' }}>
                    {cell.label}
                  </span>
                </div>
              ))}

              {/* Divider between rows */}
              <div style={{ position: 'absolute', left: 0, top: HDR_TOP_H, width: totalW, height: 1, background: borderColor }} />

              {/* Bottom row: day / week / quarter markers */}
              {botRow.map((cell, idx) => (
                <div key={idx} style={{
                  position: 'absolute',
                  left: cell.left,
                  top: HDR_TOP_H,
                  width: cell.width,
                  height: HDR_BOT_H,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: `1px solid ${borderColor}`,
                  background: cell.isWeekend
                    ? 'rgba(0,0,0,0.025)'
                    : panelBg,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}>
                  <span style={{
                    fontSize: viewMode === 'day' ? 11 : 11,
                    fontWeight: 600,
                    color: cell.isWeekend ? 'var(--cc-text-muted)' : 'var(--cc-text-secondary)',
                  }}>
                    {cell.label}
                  </span>
                </div>
              ))}

              {/* Today marker in header — line only */}
              {todayPx >= 0 && todayPx < totalW && (
                <div style={{
                  position: 'absolute', left: todayPx - 1, top: 0,
                  width: 2, height: HEADER_H,
                  background: 'var(--branding-app-primary)',
                  zIndex: 5, pointerEvents: 'none',
                }} />
              )}
            </div>
          </div>
        </div>

        {/* ── Body ─────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', overflowY: 'auto', maxHeight: 540 }}>

          {/* Sidebar: phase headers + task name list */}
          <div style={{ width: SIDEBAR_W, flexShrink: 0, borderRight: `1px solid ${borderColor}`, boxSizing: 'border-box', overflow: 'hidden' }}>
            {tasks.length === 0 ? (
              <EmptyStateSidebar />
            ) : displayItems.map((item, idx) => {
              if (item.type === 'phase') {
                const isCollapsed = collapsedPhases.has(item.phase);
                return (
                  <div
                    key={'ph-' + item.phase}
                    onClick={() => setCollapsedPhases(prev => {
                      const next = new Set(prev);
                      next.has(item.phase) ? next.delete(item.phase) : next.add(item.phase);
                      return next;
                    })}
                    style={{
                      height: ROW_H, display: 'flex', alignItems: 'center',
                      padding: '0 12px', gap: 8,
                      borderLeft: `3px solid ${item.color}`,
                      borderBottom: `1px solid ${borderColor}`,
                      background: `${item.color}0d`,
                      cursor: 'pointer', userSelect: 'none',
                    }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={item.color} strokeWidth="2.8" style={{ flexShrink: 0, transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                    <span style={{ fontSize: 10, fontWeight: 800, color: item.color, textTransform: 'uppercase', letterSpacing: '0.08em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1, minWidth: 0 }}>
                      {item.phase === '__ungrouped__' ? 'Ungrouped' : item.phase}
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: item.color, background: `${item.color}1a`, padding: '1px 6px', borderRadius: 10, flexShrink: 0, marginLeft: 6 }}>
                      {item.count}
                    </span>
                    <span style={{ flex: 1 }} />
                  </div>
                );
              }
              const { task } = item;
              const team = teams.find(t => t.name === task.team);
              const isHovered = hoveredRow === idx;
              const durDays = task.start && task.end
                ? diffDays(parseDate(task.start), parseDate(task.end)) + 1
                : null;
              return (
                <div
                  key={task.id}
                  onMouseEnter={() => setHoveredRow(idx)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{
                    height: ROW_H, display: 'flex', alignItems: 'center',
                    paddingLeft: normPhases.length > 0 ? 20 : 14,
                    paddingRight: 14,
                    gap: 8,
                    overflow: 'hidden',
                    borderBottom: `1px solid ${borderColor}`,
                    background: isHovered ? 'var(--branding-app-surface)' : cardBg,
                    transition: 'background 0.12s', cursor: 'default',
                  }}
                >
                  {/* LEFT: two-line — task name + team/area subtitle */}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2, overflow: 'hidden' }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--cc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {task.name}
                    </span>
                    {(team || task.area) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
                        {team && <div style={{ width: 7, height: 7, borderRadius: '50%', background: team.color, flexShrink: 0, boxShadow: `0 0 0 1.5px ${team.color}40` }} />}
                        {team && <span style={{ fontSize: 10, color: 'var(--cc-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1, minWidth: 0 }}>{team.name}</span>}
                        {team && task.area && <span style={{ fontSize: 10, color: 'var(--cc-text-muted)', flexShrink: 0 }}>·</span>}
                        {task.area && <span style={{ fontSize: 10, color: 'var(--cc-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1, minWidth: 0 }}>{task.area}</span>}
                      </div>
                    )}
                  </div>

                  {/* RIGHT: metric columns — fixed width, never pushed out */}
                  <span style={{ width: 40, flexShrink: 0, fontSize: 11, fontWeight: 600, color: 'var(--cc-text-secondary)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {durDays != null ? `${durDays}d` : '—'}
                  </span>
                  {hasDeps && (() => {
                    const f = floatDays.get(task.id);
                    if (f == null) return null;
                    const isNearCrit = f > 0 && f <= 5;
                    const isCrit = f <= 0;
                    return (
                      <span style={{ width: 36, flexShrink: 0, fontSize: 11, fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: isCrit ? 'rgba(239,68,68,0.85)' : isNearCrit ? 'rgba(245,158,11,0.9)' : 'var(--cc-text-muted)' }}>
                        {isCrit ? '—' : `${f}d`}
                      </span>
                    );
                  })()}
                  {hasAnyBaseline && (() => {
                    if (!task.baseline?.start || !task.start) return (
                      <>
                        <span style={{ width: 40, flexShrink: 0, fontSize: 11, textAlign: 'right', color: 'var(--cc-text-muted)' }}>—</span>
                        <span style={{ width: 40, flexShrink: 0, fontSize: 11, textAlign: 'right', color: 'var(--cc-text-muted)' }}>—</span>
                      </>
                    );
                    const ds = diffDays(parseDate(task.baseline.start), parseDate(task.start));
                    const df = task.baseline.end && task.end ? diffDays(parseDate(task.baseline.end), parseDate(task.end)) : null;
                    const varColor = (v) => v == null ? 'var(--cc-text-muted)' : v > 0 ? 'rgba(239,68,68,0.85)' : v < 0 ? 'rgba(16,185,129,0.9)' : 'var(--cc-text-muted)';
                    const varLabel = (v) => v == null ? '—' : v === 0 ? '0d' : `${v > 0 ? '+' : ''}${v}d`;
                    return (
                      <>
                        <span style={{ width: 40, flexShrink: 0, fontSize: 11, fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: varColor(ds) }}>{varLabel(ds)}</span>
                        <span style={{ width: 40, flexShrink: 0, fontSize: 11, fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: varColor(df) }}>{varLabel(df)}</span>
                      </>
                    );
                  })()}
                </div>
              );
            })}
          </div>

          {/* Timeline */}
          <div
            ref={bodyRef}
            onScroll={onBodyScroll}
            style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', position: 'relative' }}
          >
            <div style={{ position: 'relative', width: totalW, minWidth: '100%', height: displayItems.length > 0 ? displayItems.length * ROW_H : 160, minHeight: 160 }}>

              {/* Row backgrounds + phase bands + rollup bars */}
              {displayItems.map((item, idx) => {
                if (item.type === 'phase') {
                  const rowTop = idx * ROW_H;
                  const barThick = 5, capW = 6, capExtend = 8;
                  const barTopRel = Math.floor((ROW_H - barThick - capExtend) / 2);
                  const hasBar = item.phaseStart && item.phaseEnd;
                  const barLeft = hasBar ? diffDays(viewStart, parseDate(item.phaseStart)) * dayW : 0;
                  const barWidth = hasBar ? Math.max(dayW, (diffDays(parseDate(item.phaseStart), parseDate(item.phaseEnd)) + 1) * dayW) : 0;
                  return (
                    <React.Fragment key={'ph-tl-' + item.phase}>
                      <div style={{
                        position: 'absolute', left: 0, top: rowTop,
                        width: '100%', height: ROW_H,
                        background: `${item.color}08`,
                        borderBottom: `1px solid ${borderColor}`,
                        borderLeft: `3px solid ${item.color}`,
                        pointerEvents: 'none',
                      }} />
                      {hasBar && <>
                        <div style={{ position: 'absolute', left: barLeft, top: rowTop + barTopRel, width: barWidth, height: barThick, background: item.color, borderRadius: 2, pointerEvents: 'none', zIndex: 2 }} />
                        <div style={{ position: 'absolute', left: barLeft, top: rowTop + barTopRel, width: capW, height: barThick + capExtend, background: item.color, borderRadius: '0 0 3px 3px', pointerEvents: 'none', zIndex: 2 }} />
                        <div style={{ position: 'absolute', left: barLeft + barWidth - capW, top: rowTop + barTopRel, width: capW, height: barThick + capExtend, background: item.color, borderRadius: '0 0 3px 3px', pointerEvents: 'none', zIndex: 2 }} />
                      </>}
                    </React.Fragment>
                  );
                }
                const { task } = item;
                return (
                  <div
                    key={task.id + '-bg'}
                    onMouseEnter={() => setHoveredRow(idx)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      position: 'absolute', left: 0, top: idx * ROW_H,
                      width: '100%', height: ROW_H,
                      background: hoveredRow === idx
                        ? 'var(--branding-app-surface)'
                        : idx % 2 === 1 ? 'rgba(0,0,0,0.015)' : 'transparent',
                      borderBottom: `1px solid ${borderColor}`,
                      transition: 'background 0.12s',
                    }}
                  />
                );
              })}

              {/* Quarter view: week separator lines */}
              {isMonthMode && Array.from({ length: Math.ceil(totalDays / 7) + 1 }, (_, j) => {
                const xPos = j * 7 * dayW;
                if (xPos > totalW) return null;
                const d = addDays(viewStart, j * 7);
                const isMonthBound = d.getDate() <= 7;
                return (
                  <div key={'wksep-' + j} style={{
                    position: 'absolute', left: xPos, top: 0,
                    width: isMonthBound ? 1.5 : 1, height: '100%',
                    background: borderColor,
                    opacity: isMonthBound ? 0.7 : 0.35,
                    pointerEvents: 'none', zIndex: 0,
                  }} />
                );
              })}

              {/* Weekend column tints (day and week views) */}
              {(viewMode === 'day' || viewMode === 'week') && Array.from({ length: totalDays }, (_, j) => {
                const d = addDays(viewStart, j);
                if (d.getDay() !== 0 && d.getDay() !== 6) return null;
                return (
                  <div key={'we-' + j} style={{
                    position: 'absolute', left: j * dayW, top: 0,
                    width: dayW, height: '100%',
                    background: 'rgba(0,0,0,0.025)',
                    pointerEvents: 'none',
                    zIndex: 0,
                  }} />
                );
              })}

              {/* Today line (body) */}
              {todayPx >= 0 && todayPx < totalW && (
                <div style={{
                  position: 'absolute', left: todayPx - 1, top: 0,
                  width: 2, height: '100%',
                  background: 'var(--branding-app-primary)',
                  opacity: 0.25, zIndex: 1, pointerEvents: 'none',
                }} />
              )}

              {/* Dependency arrows */}
              <svg style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible', zIndex: 1 }}>
                <defs>
                  <marker id="dep-arrow" markerWidth="7" markerHeight="6" refX="6" refY="3" orient="auto">
                    <path d="M 0 0 L 7 3 L 0 6 Z" fill="rgba(148,163,184,0.7)" />
                  </marker>
                  <marker id="dep-arrow-crit" markerWidth="7" markerHeight="6" refX="6" refY="3" orient="auto">
                    <path d="M 0 0 L 7 3 L 0 6 Z" fill="rgba(239,68,68,0.85)" />
                  </marker>
                </defs>
                {displayItems.map((item, idx) => {
                  if (item.type !== 'task' || !item.task.dependsOn?.length) return null;
                  return item.task.dependsOn.map(raw => {
                    const dep = normDep(raw);
                    const predEntry = displayItems.find(it => it.type === 'task' && it.task.id === dep.id);
                    if (!predEntry) return null;
                    const predIdx = displayItems.indexOf(predEntry);
                    const predTask = predEntry.task;
                    if (!predTask.start || !predTask.end || !item.task.start) return null;
                    const y1 = predIdx * ROW_H + ROW_H / 2;
                    const y2 = idx * ROW_H + ROW_H / 2;
                    let x1, x2;
                    switch (dep.type) {
                      case 'SS':
                        x1 = diffDays(viewStart, parseDate(predTask.start)) * dayW;
                        x2 = diffDays(viewStart, parseDate(item.task.start)) * dayW + dep.lag * dayW;
                        break;
                      case 'FF':
                        x1 = (diffDays(viewStart, parseDate(predTask.end)) + 1) * dayW;
                        x2 = item.task.end ? (diffDays(viewStart, parseDate(item.task.end)) + 1) * dayW + dep.lag * dayW : x1;
                        break;
                      case 'SF':
                        x1 = diffDays(viewStart, parseDate(predTask.start)) * dayW;
                        x2 = item.task.end ? (diffDays(viewStart, parseDate(item.task.end)) + 1) * dayW + dep.lag * dayW : x1;
                        break;
                      default: // FS
                        x1 = (diffDays(viewStart, parseDate(predTask.end)) + 1) * dayW;
                        x2 = diffDays(viewStart, parseDate(item.task.start)) * dayW + dep.lag * dayW;
                    }
                    const cp = Math.max(32, Math.abs(x2 - x1) * 0.45);
                    const isCrit = criticalIds.has(dep.id) && criticalIds.has(item.task.id);
                    return (
                      <path
                        key={`dep-${dep.id}-${item.task.id}-${dep.type}`}
                        d={`M ${x1} ${y1} C ${x1 + cp} ${y1}, ${x2 - cp} ${y2}, ${x2} ${y2}`}
                        stroke={isCrit ? 'rgba(239,68,68,0.7)' : 'rgba(148,163,184,0.55)'}
                        strokeWidth={isCrit ? 2 : 1.5}
                        fill="none"
                        strokeDasharray={isCrit ? 'none' : '4 3'}
                        markerEnd={isCrit ? 'url(#dep-arrow-crit)' : 'url(#dep-arrow)'}
                      />
                    );
                  });
                })}
              </svg>

              {/* Empty state */}
              {tasks.length === 0 && <EmptyStateTimeline onAdd={() => setEditTask({})} />}

              {/* Task bars */}
              {displayItems.map((item, idx) => {
                if (item.type !== 'task') return null;
                const { task } = item;
                if (task.isMilestone ? !task.start : (!task.start || !task.end)) return null;
                const i = idx; // positional alias
                const team      = teams.find(t => t.name === task.team);
                const color     = team?.color || '#64748b';
                const s         = parseDate(task.start);
                const e         = parseDate(task.end);
                const barLeft   = diffDays(viewStart, s) * dayW;
                const barWidth  = Math.max(dayW, (diffDays(s, e) + 1) * dayW);
                const barTop    = i * ROW_H + 8;
                const barH      = ROW_H - 16;

                const isActive  = dragging?.taskId === task.id;
                const pd        = isActive ? (dragging.pendingDays || 0) : 0;
                const vLeft     = isActive && dragging.type === 'move'   ? barLeft  + pd * dayW : barLeft;
                const vWidth    = isActive && dragging.type === 'resize' ? Math.max(dayW, barWidth + pd * dayW) : barWidth;
                const status    = getTaskStatus(task, todayd);

                // ── Milestone: diamond shape ──────────────────────────────────
                if (task.isMilestone) {
                  const ds          = ROW_H - 20;
                  const cx          = vLeft + dayW / 2;
                  const cy          = i * ROW_H + ROW_H / 2;
                  const navyColor   = '#0f172a';
                  const dateLabel   = fmtShort(task.start);
                  return (
                    <React.Fragment key={task.id}>
                      <div
                        style={{
                          position: 'absolute',
                          left: cx - ds / 2, top: cy - ds / 2,
                          width: ds, height: ds,
                          transform: 'rotate(45deg)',
                          background: navyColor,
                          borderRadius: 3,
                          cursor: locked ? 'default' : isActive ? 'grabbing' : 'grab',
                          zIndex: isActive ? 10 : 3,
                          boxShadow: isActive
                            ? `0 6px 20px rgba(0,0,0,0.3)`
                            : `0 2px 8px rgba(15,23,42,0.35), inset 0 1px 0 rgba(255,255,255,0.15)`,
                          transition: isActive ? 'none' : 'box-shadow 0.15s',
                        }}
                        onPointerDown={(evt) => onBarPointerDown(evt, task, 'move')}
                        onClick={() => onBarClick(task)}
                        onContextMenu={(evt) => onBarContextMenu(evt, task)}
                        onMouseEnter={(evt) => setTooltip({ task, x: evt.clientX, y: evt.clientY })}
                        onMouseMove={(evt) => setTooltip(prev => prev ? { ...prev, x: evt.clientX, y: evt.clientY } : null)}
                        onMouseLeave={() => setTooltip(null)}
                      />
                      {/* Task name above */}
                      <div style={{
                        position: 'absolute',
                        left: cx - 52, top: cy - ds / 2 - 17,
                        width: 104, textAlign: 'center',
                        fontSize: 10, fontWeight: 700, color: navyColor,
                        pointerEvents: 'none', whiteSpace: 'nowrap',
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        letterSpacing: '0.01em',
                      }}>
                        {task.name}
                      </div>
                      {/* Date label below */}
                      <div style={{
                        position: 'absolute',
                        left: cx - 30, top: cy + ds / 2 + 4,
                        width: 60, textAlign: 'center',
                        fontSize: 9, fontWeight: 600,
                        color: 'rgba(15,23,42,0.6)',
                        pointerEvents: 'none', whiteSpace: 'nowrap',
                        letterSpacing: '0.02em',
                      }}>
                        {dateLabel}
                      </div>
                    </React.Fragment>
                  );
                }

                // ── Regular task bar ──────────────────────────────────────────
                const bsLeft  = task.baseline ? diffDays(viewStart, parseDate(task.baseline.start)) * dayW : null;
                const bsWidth = task.baseline ? Math.max(dayW, (diffDays(parseDate(task.baseline.start), parseDate(task.baseline.end)) + 1) * dayW) : null;

                return (
                  <React.Fragment key={task.id}>
                    {/* Baseline ghost bar */}
                    {task.baseline && (
                      <div style={{
                        position: 'absolute',
                        left: bsLeft, top: barTop + 2,
                        width: bsWidth, height: barH - 4,
                        borderRadius: 6,
                        border: `1.5px dashed ${color}80`,
                        background: `${color}12`,
                        pointerEvents: 'none', zIndex: 1,
                      }} />
                    )}
                  <div
                    style={{
                      position: 'absolute',
                      left: vLeft, top: barTop,
                      width: vWidth, height: barH,
                      borderRadius: 7,
                      background: color,
                      cursor: locked ? 'default' : isActive ? 'grabbing' : 'grab',
                      zIndex: isActive ? 10 : 2,
                      boxShadow: isActive
                        ? `0 6px 20px rgba(0,0,0,0.25), 0 0 0 2px ${color}60`
                        : criticalIds.has(task.id)
                          ? `0 1px 3px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.22), 0 0 0 2px rgba(239,68,68,0.6)`
                          : (hasDeps && (() => { const f = floatDays.get(task.id); return f != null && f > 0 && f <= 5; })())
                            ? `0 1px 3px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.22), 0 0 0 2px rgba(245,158,11,0.6)`
                            : `0 1px 3px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.22)`,
                      overflow: 'hidden',
                      transition: isActive ? 'none' : 'box-shadow 0.15s',
                    }}
                    onPointerDown={(evt) => onBarPointerDown(evt, task, 'move')}
                    onClick={() => onBarClick(task)}
                    onContextMenu={(evt) => onBarContextMenu(evt, task)}
                    onMouseEnter={(evt) => setTooltip({ task, x: evt.clientX, y: evt.clientY })}
                    onMouseMove={(evt) => setTooltip(prev => prev ? { ...prev, x: evt.clientX, y: evt.clientY } : null)}
                    onMouseLeave={() => setTooltip(null)}
                  >
                    {/* Gloss highlight */}
                    <div style={{
                      position: 'absolute', left: 0, top: 0, right: 0,
                      height: '45%',
                      background: 'linear-gradient(to bottom, rgba(255,255,255,0.22), rgba(255,255,255,0))',
                      borderRadius: '7px 7px 0 0',
                      pointerEvents: 'none',
                    }} />

                    {/* % Complete fill overlay */}
                    {(() => {
                      const pct = task.status === 'complete' ? 100 : (task.percentComplete ?? 0);
                      if (!pct) return null;
                      const full = pct >= 100;
                      return (
                        <div style={{
                          position: 'absolute', left: 0, top: 0,
                          height: '100%',
                          width: `${Math.min(100, pct)}%`,
                          background: full ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.15)',
                          borderRadius: full ? 7 : '7px 0 0 7px',
                          transition: 'width 0.3s ease',
                        }} />
                      );
                    })()}

                    {/* Label — hidden when bar is too narrow */}
                    {vWidth >= 44 && (
                      <div style={{
                        position: 'absolute', left: 9, right: isMonthMode ? 9 : 13, top: 0, height: '100%',
                        display: 'flex', alignItems: 'center', pointerEvents: 'none',
                      }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, color: '#fff',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          textShadow: '0 1px 3px rgba(0,0,0,0.35)', letterSpacing: '0.01em',
                        }}>
                          {task.name}
                        </span>
                      </div>
                    )}

                    {/* Delay / overdue status dot */}
                    {(status === 'atrisk' || status === 'overdue') && vWidth >= 20 && (
                      <div style={{
                        position: 'absolute', right: isMonthMode ? 3 : 16, top: '50%',
                        transform: 'translateY(-50%)',
                        width: 6, height: 6, borderRadius: '50%',
                        background: status === 'overdue' ? '#ef4444' : '#f59e0b',
                        boxShadow: status === 'overdue' ? '0 0 0 2px rgba(239,68,68,0.35)' : '0 0 0 2px rgba(245,158,11,0.35)',
                        pointerEvents: 'none', zIndex: 2,
                      }} />
                    )}

                    {/* Resize handle — hidden in quarter view (bars too narrow) */}
                    {!isMonthMode && (
                      <div
                        style={{
                          position: 'absolute', right: 0, top: 0, width: 10, height: '100%',
                          cursor: 'ew-resize',
                          background: 'rgba(0,0,0,0.16)',
                          borderRadius: '0 7px 7px 0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        onPointerDown={(evt) => { evt.stopPropagation(); onBarPointerDown(evt, task, 'resize'); }}
                        onClick={(evt) => evt.stopPropagation()}
                      >
                        <div style={{ width: 2, height: 10, borderRadius: 1, background: 'rgba(255,255,255,0.5)' }} />
                      </div>
                    )}
                  </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Minimap strip ─────────────────────────────────────────────── */}
        {tasks.length > 0 && (
          <div style={{ display: 'flex', borderTop: `1px solid ${borderColor}` }}>
            <div style={{
              width: SIDEBAR_W, flexShrink: 0, height: 36,
              borderRight: `1px solid ${borderColor}`,
              background: panelBg,
              display: 'flex', alignItems: 'center', paddingLeft: 14,
              boxSizing: 'border-box',
            }}>
              <span style={{ fontSize: 9, fontWeight: 800, color: 'var(--cc-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Overview
              </span>
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <Minimap
                displayItems={displayItems}
                teams={teams}
                totalW={totalW}
                viewStart={viewStart}
                dayW={dayW}
                scrollLeft={bodyScrollLeft}
                clientW={bodyClientW}
                criticalIds={criticalIds}
                onPointerDown={onMinimapPointerDown}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Hover tooltip ─────────────────────────────────────────────────── */}
      {tooltip && !dragging && (
        <div style={{
          position: 'fixed',
          left: tooltip.x + 14,
          top: tooltip.y - 52,
          background: 'var(--cc-text-primary)',
          color: 'var(--cc-card-bg)',
          fontSize: 12,
          borderRadius: 8,
          padding: '8px 12px',
          boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
          pointerEvents: 'none',
          zIndex: 9999,
          whiteSpace: 'nowrap',
          lineHeight: 1.6,
          minWidth: 140,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>{tooltip.task.name}</div>
          <div style={{ opacity: 0.7, fontSize: 11 }}>
            {tooltip.task.team && <span style={{ marginRight: 6 }}>{tooltip.task.team}</span>}
            {fmtShort(tooltip.task.start)} → {fmtShort(tooltip.task.end)}
          </div>
          {tooltip.task.status && tooltip.task.status !== 'not_started' && (
            <div style={{ opacity: 0.65, fontSize: 11 }}>
              {tooltip.task.status === 'in_progress' ? 'In Progress' : 'Complete'}
            </div>
          )}
        </div>
      )}

      {/* ── Context menu ──────────────────────────────────────────────────── */}
      {contextMenu && (
        <ContextMenu
          task={contextMenu.task}
          x={contextMenu.x}
          y={contextMenu.y}
          onEdit={() => { setEditTask(contextMenu.task); setContextMenu(null); }}
          onDelete={() => { onTaskDelete(contextMenu.task.id); setContextMenu(null); }}
          onStatusChange={(s) => {
            const pct = s === 'complete' ? 100 : s === 'not_started' ? 0 : (contextMenu.task.percentComplete ?? 0);
            onTaskSave({ ...contextMenu.task, status: s, percentComplete: pct });
            setContextMenu(null);
          }}
          onPercentChange={(pct) => {
            const s = pct === 100 ? 'complete' : pct === 0 ? 'not_started' : 'in_progress';
            onTaskSave({ ...contextMenu.task, percentComplete: pct, status: s });
            setContextMenu(null);
          }}
          onBaseline={() => {
            const t = contextMenu.task;
            onTaskSave({ ...t, baseline: t.baseline ? null : { start: t.start, end: t.end } });
            setContextMenu(null);
          }}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* ── Task modal ────────────────────────────────────────────────────── */}
      {editTask !== null && (
        <TaskModal
          task={editTask}
          tasks={tasks}
          teams={teams}
          projectId={projectId}
          phases={normPhases}
          areas={areas}
          onSave={(t) => { onTaskSave(t); setEditTask(null); }}
          onDelete={(id) => { onTaskDelete(id); setEditTask(null); }}
          onClose={() => setEditTask(null)}
        />
      )}
    </div>
  );
}

// ─── Empty states ─────────────────────────────────────────────────────────────
function EmptyStateSidebar() {
  return (
    <div style={{ height: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0 16px' }}>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--cc-text-muted)" strokeWidth="1.5" strokeLinecap="round" style={{ opacity: 0.5 }}>
        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--cc-text-muted)', textAlign: 'center', lineHeight: 1.4 }}>No tasks yet</span>
    </div>
  );
}

function EmptyStateTimeline({ onAdd }) {
  return (
    <div style={{
      position: 'absolute', inset: 0,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 10, pointerEvents: 'none',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--cc-text-muted)', opacity: 0.6 }}>
        Add your first task to build the schedule
      </div>
    </div>
  );
}

// ─── Context menu ─────────────────────────────────────────────────────────────
function ContextMenuItem({ label, danger, onClick, children, icon }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        width: '100%', padding: '8px 14px', border: 'none',
        background: hov ? (danger ? 'rgba(239,68,68,0.08)' : 'var(--branding-app-surface-panel)') : 'transparent',
        color: danger ? '#ef4444' : 'var(--cc-text-primary)',
        cursor: 'pointer', fontSize: 13, fontWeight: 500, textAlign: 'left',
        transition: 'background 0.1s',
      }}
    >
      {icon && <span style={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</span>}
      <span style={{ flex: 1 }}>{label}</span>
      {children}
    </button>
  );
}

function ContextMenu({ task, x, y, onEdit, onDelete, onStatusChange, onBaseline, onPercentChange, onClose }) {
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [statusOpen, setStatusOpen]       = useState(false);
  const [percentOpen, setPercentOpen]     = useState(false);

  const menuW = 200;
  const menuH = 260;
  const left  = x + menuW > window.innerWidth  ? x - menuW : x;
  const top   = y + menuH > window.innerHeight ? y - menuH : y;

  const hasBaseline = !!task.baseline;

  return (
    <div
      style={{
        position: 'fixed', left, top, zIndex: 9998,
        background: 'var(--cc-card-bg)',
        border: '1px solid var(--branding-app-surface-panel-border)',
        borderRadius: 11,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.1)',
        minWidth: menuW, overflow: 'hidden',
      }}
      onPointerDown={e => e.stopPropagation()}
    >
      {/* Task name header */}
      <div style={{ padding: '10px 14px 9px', borderBottom: '1px solid var(--branding-app-surface-panel-border)' }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--cc-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>Task</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.name}</div>
      </div>

      <div style={{ padding: '4px 0' }}>
        {/* Edit */}
        <ContextMenuItem label="Edit" onClick={onEdit} icon={
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        } />

        {/* Set Status */}
        {!task.isMilestone && (
          <>
            <ContextMenuItem
              label="Set Status"
              onClick={() => setStatusOpen(p => !p)}
              icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.4, transform: statusOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </ContextMenuItem>
            {statusOpen && (
              <div style={{ background: 'var(--branding-app-surface-panel)', borderTop: '1px solid var(--branding-app-surface-panel-border)', borderBottom: '1px solid var(--branding-app-surface-panel-border)' }}>
                {[
                  { value: 'not_started', label: 'Not Started', dot: '#94a3b8' },
                  { value: 'in_progress', label: 'In Progress', dot: '#f59e0b' },
                  { value: 'complete',    label: 'Complete',    dot: '#10b981' },
                ].map(opt => {
                  const active = (task.status || 'not_started') === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => onStatusChange(opt.value)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        width: '100%', padding: '7px 14px 7px 22px', border: 'none',
                        background: active ? `${opt.dot}18` : 'transparent',
                        color: active ? opt.dot : 'var(--cc-text-secondary)',
                        cursor: 'pointer', fontSize: 12, fontWeight: active ? 700 : 400, textAlign: 'left',
                      }}
                    >
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: opt.dot, flexShrink: 0, display: 'inline-block' }} />
                      <span style={{ flex: 1 }}>{opt.label}</span>
                      {active && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Quick % complete picker */}
        {!task.isMilestone && (task.status === 'in_progress' || (task.percentComplete ?? 0) > 0) && (
          <>
            <ContextMenuItem
              label="% Complete"
              onClick={() => setPercentOpen(p => !p)}
              icon={
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
              }
            >
              <span style={{ fontSize: 11, color: 'var(--cc-text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                {task.status === 'complete' ? 100 : (task.percentComplete ?? 0)}%
              </span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.4, transform: percentOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', marginLeft: 2 }}>
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </ContextMenuItem>
            {percentOpen && (
              <div style={{ background: 'var(--branding-app-surface-panel)', borderTop: '1px solid var(--branding-app-surface-panel-border)', borderBottom: '1px solid var(--branding-app-surface-panel-border)', padding: '8px 14px' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[0, 25, 50, 75, 100].map(pct => {
                    const cur = task.status === 'complete' ? 100 : (task.percentComplete ?? 0);
                    const active = cur === pct;
                    return (
                      <button
                        key={pct}
                        onClick={() => { onPercentChange(pct); }}
                        style={{
                          flex: 1, padding: '5px 0', border: 'none', borderRadius: 6,
                          background: active ? 'var(--branding-app-primary)' : 'var(--cc-cancel-bg)',
                          color: active ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-secondary)',
                          cursor: 'pointer', fontSize: 11, fontWeight: active ? 700 : 500,
                          transition: 'all 0.12s',
                        }}
                      >
                        {pct}%
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* Baseline */}
        {!task.isMilestone && (
          <ContextMenuItem
            label={hasBaseline ? 'Clear Baseline' : 'Set as Baseline'}
            onClick={onBaseline}
            icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="3" y1="12" x2="21" y2="12"/><polyline points="8 8 3 12 8 16"/><polyline points="16 8 21 12 16 16"/></svg>}
          />
        )}

        {/* Divider + Delete */}
        <div style={{ height: 1, background: 'var(--branding-app-surface-panel-border)', margin: '4px 0' }} />
        {!deleteConfirm ? (
          <ContextMenuItem
            label="Delete"
            danger
            onClick={() => setDeleteConfirm(true)}
            icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>}
          />
        ) : (
          <div style={{ padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 600, flex: 1 }}>Delete task?</span>
            <button onClick={onDelete} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 11px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Yes</button>
            <button onClick={() => setDeleteConfirm(false)} style={{ background: 'var(--branding-app-surface-panel)', color: 'var(--cc-text-secondary)', border: '1px solid var(--branding-app-surface-panel-border)', borderRadius: 6, padding: '4px 11px', fontSize: 12, cursor: 'pointer' }}>No</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Minimap ──────────────────────────────────────────────────────────────────
function Minimap({ displayItems, teams, totalW, viewStart, dayW, scrollLeft, clientW, criticalIds, onPointerDown }) {
  const totalH = Math.max(1, displayItems.length * ROW_H);
  const vpPct  = clientW > 0 ? Math.min(100, (clientW / totalW) * 100) : 100;
  const vpLeft = clientW > 0 ? (scrollLeft / totalW) * 100 : 0;

  return (
    <div
      style={{ position: 'relative', height: 36, cursor: 'crosshair', overflow: 'hidden', userSelect: 'none' }}
      onPointerDown={onPointerDown}
    >
      <svg width="100%" height="36" viewBox={`0 0 ${totalW} ${totalH}`} preserveAspectRatio="none" style={{ display: 'block' }}>
        {displayItems.map((item, idx) => {
          if (item.type !== 'task') return null;
          const { task } = item;
          if (!task.start || !task.end) return null;
          const team  = teams.find(t => t.name === task.team);
          const color = team?.color || '#64748b';
          const x     = diffDays(viewStart, parseDate(task.start)) * dayW;
          const w     = Math.max(dayW, (diffDays(parseDate(task.start), parseDate(task.end)) + 1) * dayW);
          const isCrit = criticalIds.has(task.id);
          return (
            <rect
              key={task.id}
              x={x} y={idx * ROW_H + ROW_H * 0.28}
              width={w} height={ROW_H * 0.44}
              fill={isCrit ? '#ef4444' : color}
              opacity={isCrit ? 0.8 : 0.6}
              rx={2}
            />
          );
        })}
      </svg>

      {/* Viewport window */}
      <div style={{
        position: 'absolute', top: 0,
        left: `${vpLeft}%`,
        width: `${vpPct}%`,
        height: '100%',
        background: 'color-mix(in srgb, var(--branding-app-primary) 12%, transparent)',
        border: '1.5px solid color-mix(in srgb, var(--branding-app-primary) 55%, transparent)',
        borderRadius: 2, pointerEvents: 'none', boxSizing: 'border-box',
      }} />
    </div>
  );
}

// ─── TaskModal ────────────────────────────────────────────────────────────────
function TaskModal({ task, tasks, teams, projectId, phases, areas = [], onSave, onDelete, onClose }) {
  const isNew = !task.id;
  const [name, setName]             = useState(task.name || '');
  const [teamName, setTeamName]     = useState(task.team || (task.isMilestone ? '' : teams[0]?.name || ''));
  const [start, setStart]           = useState(task.start || '');
  const [end, setEnd]               = useState(task.end || '');
  const [status, setStatus]         = useState(task.status || 'not_started');
  const [percentComplete, setPercentComplete] = useState(task.percentComplete ?? 0);
  const [isMilestone, setIsMilestone] = useState(task.isMilestone || false);
  const [phase, setPhase]           = useState(task.phase || '');
  const [area, setArea]             = useState(task.area || '');
  const [dependsOn, setDependsOn]   = useState((task.dependsOn || []).map(normDep));
  const [checkedIn, setCheckedIn]   = useState([]);
  const [loadingCI, setLoadingCI]   = useState(!isNew);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [nameFocused, setNameFocused]     = useState(false);

  const team = teams.find(t => t.name === teamName);

  // Fetch checked-in members
  useEffect(() => {
    if (isNew || !projectId || !team) { setLoadingCI(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const usersSnap = await getDocs(collection(db, 'projects', projectId, 'siteAccess'));
        const members = [];
        for (const userDoc of usersSnap.docs) {
          const uid = userDoc.id;
          const logSnap = await getDocs(
            query(collection(db, 'projects', projectId, 'siteAccess', uid, 'logs'), orderBy('timestamp','desc'), limit(1))
          );
          const entry = logSnap.docs[0]?.data();
          if (!entry || !entry.type?.toLowerCase().includes('in')) continue;
          const memberSnap = await getDoc(fsDoc(db, `projects/${projectId}/teams/${team.id}/memberDetails`, uid));
          if (!memberSnap.exists()) continue;
          let pic = '/crewchainDefault.png';
          try {
            const g = await getDoc(fsDoc(db, `global_backup/users/profiles/${uid}`));
            if (g.exists()) pic = g.data()?.profilePictureUrl || pic;
          } catch {}
          members.push({ uid, name: memberSnap.data()?.name || uid, pic });
        }
        if (!cancelled) { setCheckedIn(members); setLoadingCI(false); }
      } catch { if (!cancelled) setLoadingCI(false); }
    })();
    return () => { cancelled = true; };
  }, [isNew, projectId, team?.id]); // eslint-disable-line

  const canSave = name.trim() && start && (isMilestone || (teamName && end));

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      ...task,
      name: name.trim(),
      team: teamName,
      start,
      end: isMilestone ? start : end,
      status: isMilestone ? 'not_started' : status,
      percentComplete: isMilestone ? 0 : (status === 'complete' ? 100 : Math.min(100, Math.max(0, percentComplete))),
      isMilestone: isMilestone || false,
      phase: phase || '',
      area: area || '',
      dependsOn: isMilestone ? [] : dependsOn,
    });
  };

  const labelStyle = {
    display: 'block',
    fontSize: 10,
    fontWeight: 800,
    color: 'var(--cc-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.09em',
    marginBottom: 8,
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: 20 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'var(--cc-card-bg)',
        borderRadius: 18,
        width: '100%', maxWidth: 520,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 32px 80px rgba(0,0,0,0.32)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Team accent stripe */}
        <div style={{ height: 5, background: 'var(--branding-app-primary)', borderRadius: '18px 18px 0 0', flexShrink: 0 }} />

        <div style={{ padding: '26px 30px 30px' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 26 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: 'var(--cc-text-primary)', letterSpacing: '-0.01em' }}>
              {isNew ? (isMilestone ? 'New Milestone' : 'New Task') : (isMilestone ? 'Edit Milestone' : 'Edit Task')}
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'var(--branding-app-surface-panel)',
                border: '1px solid var(--branding-app-surface-panel-border)',
                borderRadius: 8,
                cursor: 'pointer',
                color: 'var(--cc-text-muted)',
                width: 32, height: 32,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--branding-app-surface)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--branding-app-surface-panel)'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>

            {/* Type toggle: Task vs Milestone */}
            <div>
              <label style={labelStyle}>Type</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { key: false, label: 'Task', icon: '▬' },
                  { key: true,  label: 'Milestone', icon: '◆' },
                ].map(opt => {
                  const active = isMilestone === opt.key;
                  return (
                    <button
                      key={String(opt.key)}
                      onClick={() => {
                        setIsMilestone(opt.key);
                        if (opt.key && start) setEnd(start);
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 7,
                        padding: '8px 16px',
                        borderRadius: 9,
                        border: active ? '2px solid var(--branding-app-primary)' : '2px solid transparent',
                        boxShadow: active ? '0 0 0 3px var(--branding-app-primary-ring)' : 'inset 0 0 0 1px var(--branding-app-surface-panel-border)',
                        background: active ? 'var(--branding-app-primary-soft)' : 'var(--branding-app-surface-panel)',
                        color: active ? 'var(--branding-app-primary)' : 'var(--cc-text-secondary)',
                        cursor: 'pointer', fontSize: 13,
                        fontWeight: active ? 700 : 500,
                        transition: 'all 0.15s',
                      }}
                    >
                      <span style={{ fontSize: 11 }}>{opt.icon}</span>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Task name */}
            <div>
              <label style={labelStyle}>Task name</label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                onFocus={() => setNameFocused(true)}
                onBlur={() => setNameFocused(false)}
                placeholder=""
                autoFocus
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '11px 14px',
                  borderRadius: 10,
                  border: nameFocused ? '2px solid var(--branding-app-primary)' : '1.5px solid var(--branding-app-surface-panel-border)',
                  background: 'var(--branding-app-surface-panel)',
                  color: 'var(--cc-text-primary)',
                  fontSize: 14,
                  fontFamily: 'inherit',
                  outline: 'none',
                  transition: 'border 0.15s',
                }}
              />
            </div>

            {/* Team */}
            <div>
              <label style={labelStyle}>
                Team{isMilestone && <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, marginLeft: 5, opacity: 0.6 }}>(optional)</span>}
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {isMilestone && (
                  <button
                    onClick={() => setTeamName('')}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 7,
                      padding: '8px 14px', borderRadius: 9,
                      border: teamName === '' ? '2px solid #1e3a5f' : '2px solid transparent',
                      boxShadow: teamName === '' ? '0 0 0 3px #1e3a5f20' : 'inset 0 0 0 1px var(--branding-app-surface-panel-border)',
                      background: teamName === '' ? '#1e3a5f18' : 'var(--branding-app-surface-panel)',
                      color: 'var(--cc-text-primary)',
                      cursor: 'pointer', fontSize: 13,
                      fontWeight: teamName === '' ? 700 : 400,
                      transition: 'all 0.15s',
                    }}
                  >
                    None
                  </button>
                )}
                {teams.map(t => {
                  const active = teamName === t.name;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTeamName(t.name)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 7,
                        padding: '8px 14px',
                        borderRadius: 9,
                        border: active ? `2px solid ${t.color}` : '2px solid transparent',
                        boxShadow: active ? `0 0 0 3px ${t.color}20` : 'inset 0 0 0 1px var(--branding-app-surface-panel-border)',
                        background: active ? `${t.color}18` : 'var(--branding-app-surface-panel)',
                        color: 'var(--cc-text-primary)',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: active ? 700 : 400,
                        transition: 'all 0.15s',
                      }}
                    >
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: t.color, flexShrink: 0, boxShadow: `0 0 0 2px ${t.color}30` }} />
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Phase — only shown when phases exist */}
            {phases && phases.length > 0 && (
              <div>
                <label style={labelStyle}>Phase</label>
                <select
                  value={phase}
                  onChange={e => setPhase(e.target.value)}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '11px 14px',
                    borderRadius: 10,
                    border: '1.5px solid var(--branding-app-surface-panel-border)',
                    background: 'var(--branding-app-surface-panel)',
                    color: 'var(--cc-text-primary)',
                    fontSize: 14, fontFamily: 'inherit',
                    outline: 'none', cursor: 'pointer',
                    appearance: 'auto',
                  }}
                >
                  <option value="">None</option>
                  {phases.map(p => {
                    const name = typeof p === 'string' ? p : p.name;
                    return <option key={name} value={name}>{name}</option>;
                  })}
                </select>
              </div>
            )}

            {/* Area — only shown when areas exist */}
            {areas.length > 0 && (
              <div>
                <label style={labelStyle}>Site Area</label>
                <select
                  value={area}
                  onChange={e => setArea(e.target.value)}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '11px 14px', borderRadius: 10,
                    border: '1.5px solid var(--branding-app-surface-panel-border)',
                    background: 'var(--branding-app-surface-panel)',
                    color: 'var(--cc-text-primary)',
                    fontSize: 14, fontFamily: 'inherit',
                    outline: 'none', cursor: 'pointer', appearance: 'auto',
                  }}
                >
                  <option value="">None</option>
                  {areas.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            )}

            {/* Dependencies */}
            {!isMilestone && tasks.filter(t => t.id !== task.id).length > 0 && (
              <div>
                <label style={labelStyle}>Depends On</label>
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: 5,
                  maxHeight: 180, overflowY: 'auto',
                  border: '1.5px solid var(--branding-app-surface-panel-border)',
                  borderRadius: 10, padding: 6,
                  background: 'var(--branding-app-surface-panel)',
                }}>
                  {tasks.filter(t => t.id !== task.id).map(t => {
                    const dep = dependsOn.find(d => d.id === t.id);
                    const sel = !!dep;
                    const tTeam = teams.find(tm => tm.name === t.team);
                    const color = tTeam?.color || 'var(--branding-app-primary)';
                    return (
                      <div key={t.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <button
                          onClick={() => setDependsOn(prev =>
                            sel ? prev.filter(d => d.id !== t.id) : [...prev, { id: t.id, type: 'FS', lag: 0 }]
                          )}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '6px 10px', borderRadius: 7, textAlign: 'left',
                            border: sel ? `1.5px solid ${color}` : '1.5px solid transparent',
                            background: sel ? `${color}12` : 'transparent',
                            color: sel ? color : 'var(--cc-text-secondary)',
                            cursor: 'pointer', fontSize: 13, fontWeight: sel ? 600 : 400,
                            transition: 'all 0.12s', width: '100%',
                          }}
                        >
                          <div style={{
                            width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                            border: sel ? `2px solid ${color}` : '2px solid var(--branding-app-surface-panel-border)',
                            background: sel ? color : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {sel && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                          </div>
                          {tTeam && <span style={{ width: 7, height: 7, borderRadius: '50%', background: tTeam.color, flexShrink: 0 }} />}
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{t.name}</span>
                        </button>
                        {sel && (
                          <div style={{ display: 'flex', gap: 6, paddingLeft: 32, alignItems: 'center' }}>
                            <select
                              value={dep.type}
                              onChange={e => setDependsOn(prev => prev.map(d => d.id === t.id ? { ...d, type: e.target.value } : d))}
                              style={{ fontSize: 11, fontWeight: 700, padding: '3px 6px', borderRadius: 6, border: `1.5px solid ${color}40`, background: `${color}10`, color, cursor: 'pointer', fontFamily: 'inherit' }}
                            >
                              <option value="FS">FS — Finish to Start</option>
                              <option value="SS">SS — Start to Start</option>
                              <option value="FF">FF — Finish to Finish</option>
                              <option value="SF">SF — Start to Finish</option>
                            </select>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontSize: 11, color: 'var(--cc-text-muted)' }}>Lag</span>
                              <input
                                type="number"
                                value={dep.lag}
                                onChange={e => setDependsOn(prev => prev.map(d => d.id === t.id ? { ...d, lag: parseInt(e.target.value) || 0 } : d))}
                                style={{ width: 52, fontSize: 11, padding: '3px 6px', borderRadius: 6, border: '1.5px solid var(--branding-app-surface-panel-border)', background: 'var(--branding-app-surface-panel)', color: 'var(--cc-text-primary)', fontFamily: 'inherit', textAlign: 'right' }}
                              />
                              <span style={{ fontSize: 11, color: 'var(--cc-text-muted)' }}>d</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {dependsOn.length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--cc-text-muted)' }}>
                    {dependsOn.length} predecessor{dependsOn.length > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            )}

            {/* Dates */}
            <div>
              <label style={labelStyle}>{isMilestone ? 'Date' : 'Dates'}</label>
              {isMilestone ? (
                <input
                  type="date"
                  value={start}
                  onChange={evt => { setStart(evt.target.value); setEnd(evt.target.value); }}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '11px 14px',
                    borderRadius: 10,
                    border: `1.5px solid var(--branding-app-surface-panel-border)`,
                    background: 'var(--branding-app-surface-panel)',
                    color: 'var(--cc-text-primary)',
                    fontSize: 14, fontFamily: 'inherit',
                    outline: 'none', colorScheme: 'inherit',
                  }}
                />
              ) : (
                <DateRangePicker
                  fullWidth
                  dropdownZIndex={3100}
                  startDate={start}
                  endDate={end}
                  onStartDateChange={setStart}
                  onEndDateChange={setEnd}
                  onClear={() => { setStart(''); setEnd(''); }}
                  placeholder="Click to select start, then end"
                />
              )}
            </div>

            {/* Status — hidden for milestones */}
            {!isMilestone && (
              <div>
                <label style={labelStyle}>Status</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { value: 'not_started', label: 'Not Started', dot: '#94a3b8' },
                    { value: 'in_progress', label: 'In Progress', dot: '#f59e0b' },
                    { value: 'complete',    label: 'Complete',    dot: '#10b981' },
                  ].map(opt => {
                    const active = status === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setStatus(opt.value);
                          if (opt.value === 'complete')    setPercentComplete(100);
                          else if (opt.value === 'not_started') setPercentComplete(0);
                        }}
                        style={{
                          flex: 1,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                          padding: '9px 12px',
                          borderRadius: 9,
                          border: active ? `2px solid ${opt.dot}` : '2px solid transparent',
                          boxShadow: active ? `0 0 0 3px ${opt.dot}20` : 'inset 0 0 0 1px var(--branding-app-surface-panel-border)',
                          background: active ? `${opt.dot}18` : 'var(--branding-app-surface-panel)',
                          color: active ? opt.dot : 'var(--cc-text-secondary)',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: active ? 700 : 500,
                          transition: 'all 0.15s',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: opt.dot, flexShrink: 0 }} />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* % Complete — only for in-progress tasks */}
            {!isMilestone && status === 'in_progress' && (
              <div>
                <label style={labelStyle}>
                  % Complete
                  <span style={{ float: 'right', fontWeight: 700, color: 'var(--cc-text-primary)', fontVariantNumeric: 'tabular-nums' }}>{percentComplete}%</span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={percentComplete}
                  onChange={e => {
                    const v = Number(e.target.value);
                    setPercentComplete(v);
                    if (v === 100) setStatus('complete');
                    else if (v === 0) setStatus('not_started');
                    else setStatus('in_progress');
                  }}
                  style={{ width: '100%', accentColor: 'var(--branding-app-primary)', cursor: 'pointer' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--cc-text-muted)', marginTop: 2 }}>
                  <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
                </div>
              </div>
            )}

            {/* Checked-in members */}
            {!isNew && (
              <div style={{ paddingTop: 18, borderTop: '1px solid var(--branding-app-surface-panel-border)' }}>
                <label style={labelStyle}>Checked In Now</label>
                {loadingCI ? (
                  <div style={{ fontSize: 13, color: 'var(--cc-text-muted)' }}>Loading…</div>
                ) : checkedIn.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--cc-text-muted)', fontStyle: 'italic' }}>
                    No team members currently on site.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {checkedIn.map(m => (
                      <div key={m.uid} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        background: 'var(--branding-app-surface-panel)',
                        borderRadius: 10, padding: '8px 14px',
                        border: '1px solid var(--branding-app-surface-panel-border)',
                      }}>
                        <img src={m.pic} alt={m.name} style={{ width: 34, height: 34, borderRadius: 8, objectFit: 'cover' }} />
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--cc-text-primary)' }}>{m.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--branding-app-surface-panel-border)' }}>
            {!isNew && !confirmDelete && (
              <button
                onClick={() => setConfirmDelete(true)}
                style={{ background: 'transparent', color: '#ef4444', border: '1.5px solid #ef4444', borderRadius: 9, padding: '9px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ef4444'; }}
              >
                Delete
              </button>
            )}
            {confirmDelete && (
              <>
                <span style={{ fontSize: 13, color: '#ef4444', fontWeight: 600 }}>Delete this task?</span>
                <button onClick={() => onDelete(task.id)} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                  Yes, delete
                </button>
                <button onClick={() => setConfirmDelete(false)} style={{ background: 'var(--branding-app-surface-panel)', color: 'var(--cc-text-primary)', border: '1.5px solid var(--branding-app-surface-panel-border)', borderRadius: 9, padding: '9px 14px', cursor: 'pointer', fontSize: 13 }}>
                  Cancel
                </button>
              </>
            )}
            <div style={{ flex: 1 }} />
            <button
              onClick={onClose}
              style={{ background: 'var(--branding-app-surface-panel)', color: 'var(--cc-text-secondary)', border: '1.5px solid var(--branding-app-surface-panel-border)', borderRadius: 9, padding: '9px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              style={{
                background: canSave ? 'var(--branding-app-primary)' : 'var(--branding-app-surface-panel-border)',
                color: canSave ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-muted)',
                border: 'none',
                borderRadius: 9,
                padding: '9px 24px',
                cursor: canSave ? 'pointer' : 'not-allowed',
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: '0.01em',
                boxShadow: canSave ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              {isNew ? 'Add Task' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
